// Helper function to format currency
// ✅ Helper function to format currency
const formatCurrency = (value) => {
  if (isNaN(value)) return null;
  return `₹${Number(value).toFixed(2)}`;
};

export const createCustomerCardData = (customer, type, paymentMode) => {
  // ---- Common Values ----
  const price = Number(customer.price) || 0;
  const quantity = Number(customer.count) || 0;
  const deliveredQty = Number(customer.delivered_quantity) || 0;
  const gstAmountFromAPI = Number(customer.gst_amount) || 0;
  const securityDepositPerUnit = Number(customer.securitydeposit) || 0;
  const creditDiscountAmount = Number(customer.credit_discount_amount) || 0;
  const couponDiscount = Number(customer.coupon_discount) || 0;
  const couponCodeId = Number(customer.coupon_code_id) || 0;

  // ✅ hasCoupon only when both conditions true
  const hasCoupon = couponDiscount > 0 && couponCodeId !== 0;

  // Determine which quantity to use based on type
  const activeQuantity = type === "4" ? deliveredQty : quantity;

  // ✅ Calculate empties received based on condition
  const mainEmpties =
    type !== "4"
      ? Number(customer.returnclinder) || 0
      : Number(customer.empty_received) || 0;

  // ✅ Check if extra cylinders exist
  const hasExtraCylinders =
    Array.isArray(customer.extra_cylinders) && customer.extra_cylinders.length > 0;

  // ✅ If extra cylinders exist, add their empties
  const extraEmpties = hasExtraCylinders
    ? customer.extra_cylinders.reduce(
        (sum, cyl) => sum + (parseInt(cyl.extra_received_empty) || 0),
        0
      )
    : 0;

  // ✅ Final empties logic
  const totalEmptiesReceived = hasExtraCylinders
    ? mainEmpties + extraEmpties
    : mainEmpties;

  // ---- Initialize calculation vars ----
  let taxableAmount = 0;
  let gstAmount = 0;
  let totalSecurityDeposit = 0;
  let calculatedTotal = 0;
  let discountedPrice = price;
  let totalCouponDiscount = 0;

  // ✅ ---- COUPON LOGIC ----
  if (hasCoupon) {
    totalCouponDiscount = couponDiscount * activeQuantity;
    discountedPrice = price - couponDiscount; // per unit
    taxableAmount = discountedPrice * activeQuantity;
    gstAmount = taxableAmount * 0.18; // 18% GST
    totalSecurityDeposit =
      (activeQuantity - totalEmptiesReceived) * securityDepositPerUnit;
    calculatedTotal = taxableAmount + gstAmount + totalSecurityDeposit;
  }

  // ✅ ---- CREDIT MODE LOGIC ----
  else if (paymentMode === "CREDIT") {
    discountedPrice = price - creditDiscountAmount;
    taxableAmount = discountedPrice * activeQuantity;
    gstAmount = taxableAmount * 0.18;
    totalSecurityDeposit =
      (activeQuantity - totalEmptiesReceived) * securityDepositPerUnit;
    calculatedTotal = taxableAmount + gstAmount + totalSecurityDeposit;
  }

  // ✅ ---- NORMAL MODE LOGIC ----
  else {
    taxableAmount = price * activeQuantity;
    gstAmount = gstAmountFromAPI;
    totalSecurityDeposit =
      (activeQuantity - totalEmptiesReceived) * securityDepositPerUnit;
    calculatedTotal = taxableAmount + gstAmount + totalSecurityDeposit;
  }

  // ---- Display Data ----
  const allData = [
    { key: "Product Name", value: customer.product_name, valueWeight: "semibold" },
    { key: "Quantity", value: quantity },
    { key: "Delivered Quantity", value: deliveredQty },
    { key: "Cancelled Quantity", value: customer.cancelled_quantity },
    { key: "Empties Received", value: totalEmptiesReceived },
    { key: "Date and Time", value: customer.datetime !== "0000-00-00 00:00:00" ? customer.datetime : null },
    { key: "Schedule Date", value: customer.scheduledate !== "0000-00-00" ? customer.scheduledate : null },
    { key: "Schedule Time", value: customer.scheduletime || null },

    ...(type === "5"
      ? [
          {
            key: "Cancel Date & Time",
            value:
              customer.cancel_datetime !== "0000-00-00 00:00:00"
                ? customer.cancel_datetime
                : null,
          },
          { key: "Cancel Reason", value: customer.cancel_reason },
          { key: "Cancelled By", value: customer.cancelled_by },
        ]
      : []),

    { key: "Price Per Unit", value: formatCurrency(price), valueWeight: "medium" },

    ...(type === "3" || type === "4"
      ? [
          {
            key: "Dispatch Date & Time",
            value:
              customer.dispatch_datetime !== "0000-00-00 00:00:00"
                ? customer.dispatch_datetime
                : null,
          },
        ]
      : []),

    ...(type === "4"
      ? [
          {
            key: "Delivered Date & Time",
            value:
              customer.deliver_datetime !== "0000-00-00 00:00:00"
                ? customer.deliver_datetime
                : null,
          },
        ]
      : []),

    ...(hasCoupon
      ? [
          { key: "Coupon Code", value: customer.coupon_code },
          { key: "Coupon Discount (Per Unit)", value: formatCurrency(couponDiscount) },
          { key: "Total Coupon Discount", value: formatCurrency(totalCouponDiscount) },
          { key: "Discounted Price (Per Unit)", value: formatCurrency(discountedPrice) },
          { key: "Taxable Amount", value: formatCurrency(taxableAmount) },
          { key: "GST (18%)", value: formatCurrency(gstAmount) },
          {
            key: "Total Security Deposit",
            value: formatCurrency(totalSecurityDeposit),
            valueWeight: "medium",
          },
          {
            key: "Total Amount (Final)",
            value: formatCurrency(calculatedTotal),
            valueWeight: "bold",
          },
        ]
      : paymentMode === "CREDIT"
      ? [
          { key: "Credit Discount (Per Unit)", value: formatCurrency(creditDiscountAmount) },
          { key: "Discounted Price (Per Unit)", value: formatCurrency(discountedPrice) },
          { key: "Taxable Amount", value: formatCurrency(taxableAmount) },
          { key: "GST (18%)", value: formatCurrency(gstAmount) },
          {
            key: "Total Security Deposit",
            value: formatCurrency(totalSecurityDeposit),
            valueWeight: "medium",
          },
          {
            key: "Total Amount (Final)",
            value: formatCurrency(calculatedTotal),
            valueWeight: "bold",
          },
        ]
      : [
          { key: "Total Price", value: formatCurrency(price * activeQuantity) },
          { key: "Taxable Amount", value: formatCurrency(taxableAmount) },
          {
            key: "Security Deposit Per Unit",
            value: formatCurrency(securityDepositPerUnit),
          },
          {
            key: "Total Security Deposit",
            value: formatCurrency(totalSecurityDeposit),
          },
          { key: "GST Amount", value: formatCurrency(gstAmountFromAPI) },
          {
            key: "Total Amount",
            value: formatCurrency(calculatedTotal),
            valueWeight: "bold",
          },
        ]),
  ];

  // ✅ Filter null/empty/₹0.00 values
  const filteredData = allData.filter(
    (item) =>
      item.value !== null &&
      item.value !== undefined &&
      item.value !== "" &&
      item.value !== "₹0.00"
  );

  // ✅ Return both display data and calculation values
  return {
    displayData: filteredData,
    calculatedTotal,
    taxableAmount,
    gstAmount,
    totalSecurityDeposit,
    discountedPrice,
    totalCouponDiscount,
  };
};

  

  /**
   * Calculate grand total for all products
   */
  export const calculateGrandTotal = (details, type, paymentMode) => {
    if (!details || details.length === 0) return 0;
  
    return details.reduce((sum, product) => {
      const productData = createCustomerCardData(product, type, paymentMode);
      return sum + (productData?.calculatedTotal || 0);
    }, 0);
  };